import { formatSalesOrderNumber } from '@order-management/format';
import { BasicSalesOrderData, SalesOrderData } from '@order-management/types';
import { useCallback, useEffect, useState } from 'react';
import { Columns, Element, Icon, Notification } from 'react-bulma-components';
import { FaDownload } from 'react-icons/fa6';
import Conditional from '../../shared/conditional/conditional';
import useHttpRequest from '../../shared/hooks/http-request';
import { useXLSXDownload } from '../../shared/hooks/xlsx-download';
import If from '../../shared/if/if';
import AnimatedChevronIcon from '../../ui/animated-chevron-icon/animated-chevron-icon';
import Button from '../../ui/button/button';
import PageSpinner from '../../ui/page-spinner/page-spinner';
import SalesOrderDetailsLayout from '../sales-order-details-layout/sales-order-details-layout';
import {
  fetchSalesOrder,
  fetchSalesOrderDownloadableData,
} from '../sales-orders-service';
import styles from './sales-order-summary-list-item.module.scss';

const EXPORT_SALES_ORDER_ERROR_MESSAGE =
  'The sales order could not be exported. Please try again';

export interface SalesOrderSummaryListItemProps {
  data: BasicSalesOrderData;
  collapsed?: boolean;
}

export function SalesOrderSummaryListItem({
  data,
  collapsed = true,
}: SalesOrderSummaryListItemProps) {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [detailsLoaded, setDetailsLoaded] = useState(false);
  const [salesOrderDetails, setSalesOrderDetails] = useState<SalesOrderData>();
  const { executeHttpRequest } = useHttpRequest();
  const {
    executeHttpRequest: executeFetchSalesOrderDownloadableData,
    isLoading: isFetchingSalesOrderDownloadableData,
  } = useHttpRequest();
  const { downloadXLSX } = useXLSXDownload();

  const fetchSalesOrderData = useCallback(async () => {
    executeHttpRequest({
      asyncFunction: async () => {
        const salesOrder = await fetchSalesOrder(data.id);
        setSalesOrderDetails(salesOrder);
        setDetailsLoaded(true);
      },
    });
  }, [data.id, executeHttpRequest]);

  const handleExportSalesOrderClick = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      executeFetchSalesOrderDownloadableData({
        asyncFunction: async () => {
          const salesOrderDownloadableData =
            await fetchSalesOrderDownloadableData(data.id);
          downloadXLSX(salesOrderDownloadableData);
        },
        customErrorMessage: EXPORT_SALES_ORDER_ERROR_MESSAGE,
      });
    },
    [data.id, downloadXLSX, executeFetchSalesOrderDownloadableData]
  );

  const handleCollapse = useCallback(() => {
    if (isCollapsed) {
      fetchSalesOrderData();
    } else {
      setDetailsLoaded(false);
    }
    setIsCollapsed(!isCollapsed);
  }, [fetchSalesOrderData, isCollapsed]);

  useEffect(() => {
    if (!collapsed) {
      fetchSalesOrderData();
    }
  }, [collapsed, fetchSalesOrderData]);

  return (
    <Element>
      <Columns
        multiline={false}
        className={styles.salesOrderItem}
        onClick={handleCollapse}
      >
        <Columns.Column
          display="flex"
          alignItems="center"
          className="gap-7"
          size={11}
        >
          <Element display="flex" alignItems="center" className="gap-6">
            <Element
              renderAs="button"
              type="button"
              aria-expanded={!isCollapsed}
              display="flex"
              alignItems="center"
              className={`gap-2 ${styles.toggle}`}
            >
              <Element className="is-clickable">
                <Icon color="primary-dark">
                  <AnimatedChevronIcon active={!isCollapsed} />
                </Icon>
              </Element>
              <span className="is-size-4 has-text-weight-light has-text-primary-dark">
                {formatSalesOrderNumber(data.number)}
              </span>
            </Element>
            <span className="is-size-6">
              {data.devicesCount}{' '}
              {data.devicesCount === 1 ? 'device' : 'devices'}
              {' - '}
              {data.licensingProductGroupsCount}{' '}
              {data.licensingProductGroupsCount === 1
                ? 'software title'
                : 'software titles'}
            </span>
            <Button
              color="primary-dark"
              className={styles.exportButton}
              onClick={handleExportSalesOrderClick}
              isExecutingAction={isFetchingSalesOrderDownloadableData}
            >
              <Button.LoadingIcon icon={FaDownload} />
              <span>Export</span>
            </Button>
            <Notification
              color="primary-light"
              marginless
              className={styles.permissionsGroupPill}
            >
              <span className="has-text-primary-dark is-size-7">
                {data.permissionsGroup}
              </span>
            </Notification>
          </Element>
        </Columns.Column>
      </Columns>

      <If condition={!isCollapsed}>
        <Conditional condition={detailsLoaded}>
          <Conditional.True>
            <SalesOrderDetailsLayout salesOrder={salesOrderDetails!} />
          </Conditional.True>
          <Conditional.False>
            <PageSpinner />
          </Conditional.False>
        </Conditional>
      </If>

      <hr className="my-4 has-background-grey-lighter" />
    </Element>
  );
}

export default SalesOrderSummaryListItem;
